import { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import isEqual from 'lodash/isEqual'

import useSessionSaver from './useSessionSaver'
import { selectCloningStep } from '../reducers/cloning_steps'
import { selectCloneLeaguesSessionData } from '../reducers/session_saver'
import { CloningStepsEnum } from '../constants'
import { resetFilters } from '../reducers/leagues_filter_values'
import { fetchEnhancedLeaguesFromList } from '../reducers/enhanced_leagues'
import { selectSelectedLeaguesForCloning } from '../reducers/selected_leagues_for_cloning'

const useCloningStepsEffects = () => {
  const dispatch = useDispatch()
  const {
    saveSelectedLeaguesToSession,
    saveSessionToServer,
    saveConfigsToSession,
    prePopulateMassDates,
  } = useSessionSaver()

  const selectedLeaguesForCloning = useSelector(selectSelectedLeaguesForCloning)
  const targetLeaguesIDs = selectedLeaguesForCloning.map(league => league.id)

  const cloningStep = useSelector(selectCloningStep)
  const sessionData = useSelector(selectCloneLeaguesSessionData, isEqual)
  const prevStepRef = useRef(cloningStep)
  const pendingSaveRef = useRef(false)

  // Check for step transitions and react accordingly.
  // Transitions that mutate local session state use pendingSaveRef so the server
  // save fires after the Redux state has settled (second useEffect below).
  // Transitions with no local mutation call saveSessionToServer directly so the
  // updated cloningStep is always persisted regardless.
  useEffect(() => {
    const prevStep = prevStepRef.current
    prevStepRef.current = cloningStep

    // BROWSE → RENAME: commit selected leagues and fetch their full data
    if (prevStep === CloningStepsEnum.BROWSE && cloningStep === CloningStepsEnum.RENAME) {
      saveSelectedLeaguesToSession()
      dispatch(resetFilters())
      dispatch(fetchEnhancedLeaguesFromList(targetLeaguesIDs))
      pendingSaveRef.current = true
      return
    }

    // RENAME → CONFIG: pre-populate round dates (only transition that should do this)
    if (prevStep === CloningStepsEnum.RENAME && cloningStep === CloningStepsEnum.CONFIG) {
      prePopulateMassDates()
      pendingSaveRef.current = true
      return
    }

    // Leaving CONFIG (forward or back): commit config options
    if (prevStep === CloningStepsEnum.CONFIG) {
      saveConfigsToSession()
      pendingSaveRef.current = true
      return
    }

    // All other transitions (back-nav without local mutations): save directly
    saveSessionToServer()
  }, [ cloningStep ])

  // Persist to server after sessionData has updated from the league save
  useEffect(() => {
    if (pendingSaveRef.current) {
      pendingSaveRef.current = false
      saveSessionToServer()
    }
  }, [ sessionData ])
}

export default useCloningStepsEffects
